import { ButtonV2 } from "#src/commonV2/Button";
import { ModalV2 } from "#src/commonV2/modal/ModalV2";
import { TagsInput } from "#src/commonV2/TagsInput";
import dlpService from "#src/services/endpoint/dlp";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface IEditContentTagModalProps {
  workspaceId: string;
  open: boolean;
  onClose: () => void;
  onRefresh: () => void;
  savedTags: { id: string; name: string }[];
}

export const EditContentTagModal = ({
  workspaceId,
  open,
  onClose,
  onRefresh,
  savedTags,
}: IEditContentTagModalProps) => {
  const { t } = useTranslation("endpoint", {
    keyPrefix: "dlp.dataLabeling.dataLabel.content.modal",
  });

  // --------------- STATES ---------------

  // A list of tags that are being shown in the tag editor. This should be temporary until user clicking the save button.
  const [tags, setTags] = useState<{ id: string; name: string }[]>([]);
  // Submit change can take time, so this loading state helps improve UX.
  const [isLoading, setLoading] = useState<boolean>(false);

  // --------------- METHOD ---------------

  const onSaveChange = async () => {
    setLoading(true);
    const addedTags = tags.filter((tag) => !savedTags.includes(tag));

    if (addedTags.length > 0) {
      await dlpService
        .createDataLabelContent(
          workspaceId,
          addedTags.map((tag) => tag.name)
        )
        .catch(apiErrorHandler);
    }

    const removedTags = savedTags.filter((tag) => !tags.includes(tag));

    if (removedTags.length > 0) {
      await dlpService
        .deleteDataLabelContent(
          workspaceId,
          removedTags.map((tag) => tag.id)
        )
        .catch(apiErrorHandler);
    }

    setLoading(false);
    onCloseAndCleanup();
    onRefresh();
  };

  const onCloseAndCleanup = () => {
    onClose();
    setTags(savedTags);
  };

  // --------------- EFFECTS ---------------

  useEffect(() => {
    setTags(savedTags);
  }, [savedTags]);

  // --------------- RENDER ---------------

  return (
    <ModalV2 open={open} onChangeOpen={onCloseAndCleanup} className="w-[48rem]">
      <span className="text-lg font-semibold text-gray-v2-900">
        {t("title")}
      </span>
      <div className="flex flex-col gap-6">
        <span className="text-md text-gray-v2-600">{t("hint")}</span>
        <TagsInput
          tags={tags.map((tag) => tag.name)}
          // TODO: might want to check duplicate.
          setTags={(newTags) => {
            setTags((prev) => {
              const removedTags = prev.filter((tag) =>
                newTags.includes(tag.name)
              );
              const addedTags = newTags.filter(
                (tag) => !prev.map((t) => t.name.trim()).includes(tag.trim())
              );
              return [
                ...removedTags,
                ...addedTags.map((tag) => ({ id: "", name: tag })),
              ];
            });
          }}
          placeholder={t("fields.contentTag.placeholder")}
        />
        <span className="text-md font-semibold text-warning-v2">
          {t("note")}
        </span>
        <div className="flex justify-end gap-2">
          <ButtonV2
            variant="secondary"
            color="gray"
            onClick={onCloseAndCleanup}
          >
            {t("button.cancel")}
          </ButtonV2>
          <ButtonV2 className="w-20" onClick={onSaveChange} pending={isLoading}>
            {t("button.save")}
          </ButtonV2>
        </div>
      </div>
    </ModalV2>
  );
};
